import { Injectable } from '@angular/core';
import { Observable, from, of } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';
import {
  PublicClientApplication,
  AuthenticationResult,
  AccountInfo,
  Configuration,
  InteractionRequiredAuthError
} from '@azure/msal-browser';

import { ACTIVE_DIRECTORY } from '@app/shared/constants';
import { ActiveDirectoryService } from '@app/api/api-services/active-directory.service';
import { RouteHelperService } from '@app/shared/services/route-helper.service';
import { ActiveDirectoryType } from '@shared/enums';

@Injectable()
export class ActiveDirectoryConfigService {
  private link: string;
  private msalInstance: PublicClientApplication = null;
  private msalConfig: Configuration = null;
  msalAccount: AccountInfo | null = null;
  private interactionInProgress = false;
  private readonly defaultScopes = ['profile', 'email', 'offline_access', 'openid'];
  private adType: ActiveDirectoryType;
  private useAccessToken: boolean;

  constructor(
    private readonly activeDirectoryService: ActiveDirectoryService,
    private readonly routeHelper: RouteHelperService
  ) {}

  public init(): Observable<boolean> {
    const link = this.routeHelper.getRouteParam('site') as string;

    return this.activeDirectoryService.getActiveDirectorySettings(link).pipe(
      mergeMap(async (settings) => {
        if (!this.msalInstance) {
          this.msalConfig = {
            auth: {
              clientId: settings.ClientId,
              redirectUri: `${window.location.origin}/app/login/ad/${settings.Link}`,
              authority: `https://login.microsoftonline.com/${
                settings.Type === ActiveDirectoryType.Azure ? settings.Tenant : ACTIVE_DIRECTORY.adfsTenant
              }`
            },
            cache: {
              cacheLocation: 'localStorage',
              storeAuthStateInCookie: false
            }
          };
          this.useAccessToken = settings.UseAccessToken;
          if (this.useAccessToken) {
            this.defaultScopes.push(`api://${settings.ClientId}/access_as_user`);
          }
          this.msalInstance = new PublicClientApplication(this.msalConfig);
          if (typeof this.msalInstance.initialize === 'function') {
            await this.msalInstance.initialize();
          }
        }
        await this.msalInstance.handleRedirectPromise();
        this.link = settings.Link;
        this.adType = settings.Type;
        this.interactionInProgress = false;
        return true;
      }),
      mergeMap((result) => of(result)),
      catchError(() => of(false))
    );
  }

  public login(): void {
    if (this.interactionInProgress) return;
    this.init().subscribe((initialized) => {
      if (initialized) {
        this.interactionInProgress = true;
        const extraQueryParameters: any = {
          prompt: localStorage.getItem(ACTIVE_DIRECTORY.promptParameter) || 'select_account'
        };
        if (this.adType === ActiveDirectoryType.Adfs) {
          extraQueryParameters.response_mode = 'form_post';
        }
        this.msalInstance.loginRedirect({
          scopes: this.defaultScopes,
          extraQueryParameters
        });
      }
    });
  }

  public logout(): void {
    if (this.interactionInProgress) return;
    this.interactionInProgress = true;
    this.init().subscribe((initialized) => {
      if (initialized && this.isAuthenticated) {
        this.msalInstance.logoutRedirect({ postLogoutRedirectUri: this.getLogOutUri() }).finally(() => {
          this.interactionInProgress = false;
        });
      }
    });
  }

  private getLogOutUri(): string {
    const account = this.msalInstance?.getAllAccounts()?.[0];
    const idToken = (account as any)?.idToken ?? localStorage.getItem('msal.idtoken') ?? '';
    const authority = this.msalConfig?.auth?.authority ?? `https://login.microsoftonline.com/${this.userInfo.tenantId}`;
    const tenant = authority.split('/').pop() ?? '';
    const postLogoutRedirectUri = encodeURIComponent(`${window.location.origin}/app/login/ad/${this.link}`);
    const parameters = `post_logout_redirect_uri=${postLogoutRedirectUri}&id_token_hint=${idToken}`;
    const instance = authority.replace(tenant, '');
    return `${instance}${tenant}/oauth2/logout?${parameters}`;
  }

  public handleFail(): void {
    localStorage.clear();
    localStorage.setItem(ACTIVE_DIRECTORY.promptParameter, 'login');
    this.link = null;
    this.msalAccount = null;
    this.msalInstance = null;
    this.interactionInProgress = false;
  }

  public handleLogin(): void {
    localStorage.removeItem(ACTIVE_DIRECTORY.promptParameter);
  }

  public get userInfo(): AccountInfo | null {
    if (!this.msalInstance) return null;
    const accounts = this.msalInstance.getAllAccounts();
    return accounts.length > 0 ? accounts[0] : null;
  }

  public get accessToken(): Observable<string> {
    return this.init().pipe(
      mergeMap((initialized) => {
        if (!initialized) {
          return of('');
        }
        const account = this.msalInstance.getAllAccounts()[0];
        if (!account) {
          return of('');
        }

        return from(
          this.msalInstance.acquireTokenSilent({
            scopes: this.defaultScopes,
            account
          })
        ).pipe(
          map((result: AuthenticationResult) => {
            this.msalAccount = result.account;
            return this.useAccessToken ? result.accessToken || '' : result.idToken || '';
          }),
          catchError((error) => {
            if (error instanceof InteractionRequiredAuthError) {
              this.msalInstance.acquireTokenRedirect({
                scopes: this.defaultScopes
              });
            }
            return of('');
          })
        );
      })
    );
  }

  public get isAuthenticated(): boolean {
    if (!this.msalInstance) return false;
    try {
      const accounts = this.msalInstance.getAllAccounts();
      return accounts && accounts.length > 0;
    } catch {
      return false;
    }
  }
}
